import { Avatar, theme } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import type {
  HTMLAttributes,
  ReactNode,
  TdHTMLAttributes,
  ThHTMLAttributes
} from 'react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { formatDisplayName } from 'screens/Rostering/Reconciliation/reconciliationUtils'
import DeactivatedIcon from 'screens/Rostering/RotaBuilder/common/components/icons/DeactivatedIcon'
import DeletedIcon from 'screens/Rostering/RotaBuilder/common/components/icons/DeletedIcon'
import styled from 'styled-components'
import type { PayReportDrawerTableRow } from '../types/payReportDrawer'
import {
  payReportTableComponents,
  StyledPayReportTable
} from './PayReportTableComponents'
import { parseMoneySort } from './payReportBreakdownFormatters'
import { PAY_REPORT_DELETED_TM_SUPPORT_URL } from './payReportTeamMemberStatus'

type Translate = (key: string, options?: Record<string, unknown>) => string

function getDrawerHeaderTestProps(
  dataColumn: string
): HTMLAttributes<HTMLTableCellElement> &
  ThHTMLAttributes<HTMLTableCellElement> {
  return {
    'data-testid': 'drawer-col-header',
    'data-column': dataColumn
  } as HTMLAttributes<HTMLTableCellElement> &
    ThHTMLAttributes<HTMLTableCellElement>
}

function getDrawerCellTestProps(
  dataColumn: string
): HTMLAttributes<HTMLTableCellElement> &
  TdHTMLAttributes<HTMLTableCellElement> {
  return {
    'data-testid': 'drawer-col-cell',
    'data-column': dataColumn
  } as HTMLAttributes<HTMLTableCellElement> &
    TdHTMLAttributes<HTMLTableCellElement>
}

function residentStatusLabelForServiceUser(
  status: string,
  t: Translate
): string {
  const s = status.toLowerCase()
  if (s === 'inactive') {
    return t('INACTIVE')
  }
  if (s === 'hospitalised') {
    return t('RESIDENT_STATUS_HOSPITALISED')
  }
  return status
    .split(/[-_]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
}

const eventKindOrder: Record<string, number> = {
  visit: 0,
  shift: 1,
  absence: 2
}

function serviceUserSortValue(row: PayReportDrawerTableRow): string {
  if (!row.serviceUser?.name) {
    return ''
  }
  return formatDisplayName(row.serviceUser.name).toLowerCase()
}

function renderMoneyWithSuffix(
  value: string,
  suffix: string
): string | JSX.Element {
  if (value === '-') {
    return <CenteredPlaceholder>-</CenteredPlaceholder>
  }
  return `${value} ${suffix}`
}

const PersonWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`

const IconWrap = styled.span`
  width: 14px;
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
`

const TooltipTitle = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color11};
`

const UnderlinedLink = styled.a`
  text-decoration: underline;
  color: ${({ theme }) => theme.color.status.primary};
`

function getPayReportBreakdownColumns(
  t: Translate,
  nominative: string,
  showTravelTimePayColumn: boolean,
  showMileagePayColumn: boolean
): ColumnsType<PayReportDrawerTableRow> {
  const travelTimePayColumn: ColumnsType<PayReportDrawerTableRow>[number] = {
    title: (
      <HeaderCell weight="medium">
        {t('PAY_REPORT_BREAKDOWN_COL_TRAVEL_TIME_PAY')}
      </HeaderCell>
    ),
    key: 'travelTimePay',
    sorter: (a, b) =>
      parseMoneySort(a.travelTimePay) - parseMoneySort(b.travelTimePay),
    width: 120,
    align: 'right',
    onHeaderCell: () => getDrawerHeaderTestProps('travel-time-pay'),
    onCell: () => getDrawerCellTestProps('travel-time-pay'),
    render: (_, record) => (
      <BodyCell alignRight>
        {renderMoneyWithSuffix(
          record.travelTimePay,
          t('RECONCILIATION_GBP_SUFFIX')
        )}
      </BodyCell>
    )
  }

  const mileagePayColumn: ColumnsType<PayReportDrawerTableRow>[number] = {
    title: (
      <HeaderCell weight="medium">
        {t('PAY_REPORT_BREAKDOWN_COL_MILEAGE_PAY')}
      </HeaderCell>
    ),
    key: 'mileagePay',
    sorter: (a, b) =>
      parseMoneySort(a.mileagePay) - parseMoneySort(b.mileagePay),
    width: 110,
    align: 'right',
    onHeaderCell: () => getDrawerHeaderTestProps('mileage-pay'),
    onCell: () => getDrawerCellTestProps('mileage-pay'),
    render: (_, record) => (
      <BodyCell alignRight>
        {renderMoneyWithSuffix(
          record.mileagePay,
          t('RECONCILIATION_GBP_SUFFIX')
        )}
      </BodyCell>
    )
  }

  return [
    {
      title: (
        <HeaderCell weight="medium">
          {t('RECONCILIATION_TABLE_DATE')}
        </HeaderCell>
      ),
      key: 'date',
      sorter: (a, b) =>
        dayjs(a.sortDateTime).valueOf() - dayjs(b.sortDateTime).valueOf(),
      defaultSortOrder: 'ascend',
      width: 100,
      onHeaderCell: () => getDrawerHeaderTestProps('date'),
      onCell: () => getDrawerCellTestProps('date'),
      render: (_, record) => (
        <BodyCell>{dayjs(record.date).format('DD-MM-YYYY')}</BodyCell>
      )
    },
    {
      title: <HeaderCell weight="medium">{nominative}</HeaderCell>,
      key: 'serviceUser',
      sorter: (a, b) =>
        serviceUserSortValue(a).localeCompare(serviceUserSortValue(b)),
      width: 140,
      onHeaderCell: () => getDrawerHeaderTestProps('service-user'),
      onCell: () => getDrawerCellTestProps('service-user'),
      render: (_, record) => {
        const person = record.serviceUser
        if (!person?.name) {
          return <BodyCell>-</BodyCell>
        }
        const name = formatDisplayName(person.name)
        const st = person.status?.toLowerCase()
        const isDeleted = person.deleted === true || st === 'deleted'
        const showStatusIcon = !isDeleted && Boolean(st) && st !== 'active'

        return (
          <PersonWrap>
            <Avatar url={person.picture} size={22} />
            <BodyCell>{name}</BodyCell>
            {isDeleted ? (
              <Tooltip
                color={theme.color.neutral.color3}
                placement="top"
                title={
                  <TooltipTitle>
                    {t('SU_DELETED_TOOLTIP', { nominative })}{' '}
                    <UnderlinedLink
                      href={PAY_REPORT_DELETED_TM_SUPPORT_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('HERE')}
                    </UnderlinedLink>
                    .
                  </TooltipTitle>
                }
              >
                <IconWrap>
                  <DeletedIcon />
                </IconWrap>
              </Tooltip>
            ) : showStatusIcon ? (
              <Tooltip
                color={theme.color.neutral.color3}
                placement="top"
                title={
                  <TooltipTitle>
                    {t('SU_DEACTIVATED_TOOLTIP', {
                      nominative,
                      residentStatus: residentStatusLabelForServiceUser(
                        person.status ?? '',
                        t
                      )
                    })}
                  </TooltipTitle>
                }
              >
                <IconWrap>
                  <DeactivatedIcon />
                </IconWrap>
              </Tooltip>
            ) : null}
          </PersonWrap>
        )
      }
    },
    {
      title: (
        <HeaderCell weight="medium">
          {t('RECONCILIATION_TABLE_TYPE_OF_EVENT')}
        </HeaderCell>
      ),
      key: 'eventTypeName',
      sorter: (a, b) => {
        const k = eventKindOrder[a.eventKind] - eventKindOrder[b.eventKind]
        if (k !== 0) {
          return k
        }
        return a.eventTypeName.localeCompare(b.eventTypeName)
      },
      width: 148,
      onHeaderCell: () => getDrawerHeaderTestProps('event-type'),
      onCell: () => getDrawerCellTestProps('event-type'),
      render: (_, record) => <BodyCell>{record.eventTypeName}</BodyCell>
    },
    {
      title: (
        <HeaderCell weight="medium">
          {t('RECONCILIATION_TABLE_PAID_TIME')}
        </HeaderCell>
      ),
      key: 'paidTime',
      sorter: (a, b) => {
        const da = a.paidDurationMinutes ?? 0
        const db = b.paidDurationMinutes ?? 0
        if (da !== db) {
          return da - db
        }
        return dayjs(a.sortDateTime).valueOf() - dayjs(b.sortDateTime).valueOf()
      },
      width: 120,
      onHeaderCell: () => getDrawerHeaderTestProps('paid-time'),
      onCell: () => getDrawerCellTestProps('paid-time'),
      render: (_, record) => {
        if (record.paidTimeMode === 'not_paid') {
          return <NotPaidText>{t('RECONCILIATION_NOT_PAID')}</NotPaidText>
        }
        if (record.paidTimeMissed) {
          return <NotPaidText>{t('MISSED')}</NotPaidText>
        }
        const modeLabel =
          record.paidTimeMode === 'planned'
            ? t('RECONCILIATION_PLANNED')
            : record.paidTimeMode === 'actual'
              ? t('RECONCILIATION_ACTUAL')
              : ''
        return (
          <PaidStack>
            {modeLabel ? (
              <PaidTimeLabel weight="medium" level={2}>
                {modeLabel}
              </PaidTimeLabel>
            ) : null}
            {record.paidTimeOngoing && record.paidTimeClockIn ? (
              <>
                <BodyCell level={2}>
                  {t('RECONCILIATION_ACTUAL_TIME_CLOCK_IN', {
                    time: record.paidTimeClockIn
                  })}
                </BodyCell>
                <BodyCell level={2}>
                  {t('RECONCILIATION_ACTUAL_TIME_ONGOING')}
                </BodyCell>
              </>
            ) : null}
            {record.paidTimeRange ? (
              <BodyCell level={2}>{record.paidTimeRange}</BodyCell>
            ) : null}
            {record.paidDuration ? (
              <BodyCell level={2}>{record.paidDuration}</BodyCell>
            ) : null}
          </PaidStack>
        )
      }
    },
    {
      title: (
        <HeaderCell weight="medium">
          {t('RECONCILIATION_TABLE_PAY_RATE')}
        </HeaderCell>
      ),
      key: 'payRate',
      sorter: (a, b) => parseMoneySort(a.payRate) - parseMoneySort(b.payRate),
      width: 130,
      onHeaderCell: () => getDrawerHeaderTestProps('pay-rate'),
      onCell: () => getDrawerCellTestProps('pay-rate'),
      render: (_, record) => <BodyCell>{record.payRate}</BodyCell>
    },
    {
      title: (
        <HeaderCell weight="medium">
          {t('RECONCILIATION_TABLE_PAY_AMOUNT')}
        </HeaderCell>
      ),
      key: 'payAmount',
      sorter: (a, b) =>
        parseMoneySort(a.payAmount) - parseMoneySort(b.payAmount),
      width: 110,
      align: 'right',
      onHeaderCell: () => getDrawerHeaderTestProps('pay-amount'),
      onCell: () => getDrawerCellTestProps('pay-amount'),
      render: (_, record) => (
        <BodyCell alignRight>
          {renderMoneyWithSuffix(
            record.payAmount,
            t('RECONCILIATION_GBP_SUFFIX')
          )}
        </BodyCell>
      )
    },
    ...(showTravelTimePayColumn ? [travelTimePayColumn] : []),
    ...(showMileagePayColumn ? [mileagePayColumn] : []),
    {
      title: (
        <HeaderCell weight="medium">
          {t('PAY_REPORT_BREAKDOWN_COL_TOTAL_PAY')}
        </HeaderCell>
      ),
      key: 'totalPay',
      sorter: (a, b) => parseMoneySort(a.totalPay) - parseMoneySort(b.totalPay),
      width: 110,
      align: 'right',
      onHeaderCell: () => getDrawerHeaderTestProps('total-pay'),
      onCell: () => getDrawerCellTestProps('total-pay'),
      render: (_, record) => (
        <BodyCell alignRight>
          {renderMoneyWithSuffix(
            record.totalPay,
            t('RECONCILIATION_GBP_SUFFIX')
          )}
        </BodyCell>
      )
    }
  ]
}

type PayReportBreakdownTableProps = {
  rows: PayReportDrawerTableRow[]
  loading?: boolean
  nominative: string
  showTravelTimePayColumn: boolean
  showMileagePayColumn: boolean
}

export const PayReportBreakdownTable = ({
  rows,
  loading,
  nominative,
  showTravelTimePayColumn,
  showMileagePayColumn
}: PayReportBreakdownTableProps) => {
  const { t } = useTranslation()
  const drawerTableComponents = useMemo(
    () => ({
      ...payReportTableComponents,
      table: ({ children, ...props }: { children: ReactNode }) => (
        <PayReportDrawerTable data-testid="pay-report-drawer-table" {...props}>
          {children}
        </PayReportDrawerTable>
      )
    }),
    []
  )
  const columns = useMemo(
    () =>
      getPayReportBreakdownColumns(
        t,
        nominative,
        showTravelTimePayColumn,
        showMileagePayColumn
      ),
    [t, nominative, showTravelTimePayColumn, showMileagePayColumn]
  )
  return (
    <StyledPayReportTable
      components={drawerTableComponents}
      columns={columns}
      dataSource={rows}
      rowKey="id"
      loading={loading}
      pagination={false}
      scroll={{ x: 'max-content' }}
      size="small"
      showSorterTooltip={false}
      sortDirections={['ascend', 'descend']}
      rowClassName={(_, index) => (index % 2 === 0 ? 'even-row' : 'odd-row')}
    />
  )
}

const PayReportDrawerTable = styled.table`
  background: ${({ theme }) => theme.color.background.tertiary};
`

const HeaderCell = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const BodyCell = styled(Text)<{ alignRight?: boolean }>`
  color: ${({ theme }) => theme.color.neutral.color3};
  ${({ alignRight }) => (alignRight ? 'text-align: right; display: block;' : '')}
`

const PaidTimeLabel = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const NotPaidText = styled(Text)`
  color: #f5222d;
`

const PaidStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const CenteredPlaceholder = styled.span`
  display: block;
  width: 100%;
  text-align: center;
`
